import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PLANS } from '../config/plans';
import { formatCents } from './AdminFinancials';

// Platform Admin "Plan pricing" card (GET /api/admin/plans,
// PUT /api/admin/plans/:planId/price, migration 092).
//
// A change creates a new Stripe Price; the server writes nothing unless Stripe
// accepts it. Amounts travel in cents and are typed here in dollars.
//
// THE DRIFT WARNING. The public pages pre-render with the static prices in
// config/plans.js (and public/llms.txt types them out by hand). They overlay
// the live price from GET /api/plans once loaded, but crawlers and the first
// paint see the static figure. So when any active price differs from
// config/plans.js the card says the client needs updating.

export const CONFIRM_TEXT =
  'This creates a new price in Stripe. New signups pay the new price. Current subscribers keep their price.';
export const DRIFT_TEXT =
  'Public pages still show the old default. Ask for a client update to client/src/config/plans.js and public/llms.txt.';

const api = (path) => `${process.env.REACT_APP_API_URL}${path}`;

// "29", "29.5" or "29.50" in dollars, to cents. null when it is not a price.
export const dollarsToCents = (text) => {
  const s = String(text || '').trim().replace(/^\$/, '');
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
};

const staticCents = (planId) => {
  const plan = PLANS.find((p) => p.id === planId);
  return plan ? Math.round(plan.price * 100) : null;
};

export const driftedPlans = (plans) =>
  (plans || []).filter((p) => p.active && staticCents(p.planId) !== p.active.amountCents);

const centralDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', {
  timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
}) : null);

function PlanRow({ plan, onSaved }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const since = centralDate(plan.active.since);
  const inputId = `plan-price-${plan.planId}`;

  const save = async () => {
    setError('');
    const cents = dollarsToCents(value);
    if (cents === null) { setError('Enter a price in dollars, for example 29 or 29.50.'); return; }
    if (cents > 99900) { setError('The price cannot be more than $999.'); return; }
    if (cents < 50) { setError('The price must be at least $0.50.'); return; }
    if (cents === plan.active.amountCents) { setError('That is already the current price.'); return; }
    if (!window.confirm(CONFIRM_TEXT)) return;
    setSaving(true);
    try {
      await axios.put(api(`/api/admin/plans/${plan.planId}/price`), { amountCents: cents });
      setValue('');
      await onSaved();
    } catch (err) {
      const body = err.response && err.response.data;
      const stripe = body && body.stripe && (body.stripe.type || body.stripe.code)
        ? ` (Stripe: ${[body.stripe.type, body.stripe.code].filter(Boolean).join(', ')})` : '';
      setError(((body && body.error) || 'Could not change the price. Please try again.') + stripe);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="pp-row">
      <div className="pp-main">
        <div className="pp-plan">
          <span className="pp-name">{plan.name}</span>
          <span className="pp-amount">{formatCents(plan.active.amountCents)}/mo</span>
          <span className="pp-meta">
            {since ? `since ${since}` : 'default from server config'}
            {plan.active.note ? ` · ${plan.active.note}` : ''}
          </span>
        </div>
        <div className="pp-edit">
          <label htmlFor={inputId} className="pp-label">New price</label>
          <div className="pp-input-wrap">
            <span aria-hidden="true">$</span>
            <input
              id={inputId}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder={(plan.active.amountCents / 100).toFixed(2)}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={saving}
            />
          </div>
          <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving || !value.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      {error && <div className="alert alert-danger pp-error" role="alert">{error}</div>}
      {plan.history.length > 0 && (
        <div className="pp-history">
          <button
            type="button"
            className="pp-history-toggle"
            aria-expanded={showHistory}
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? 'Hide history' : `History (${plan.history.length})`}
          </button>
          {showHistory && (
            <ul>
              {plan.history.map((h) => (
                <li key={h.id}>
                  <span>{formatCents(h.amountCents)}/mo{h.active ? ' (current)' : ''}</span>
                  <span className="pp-meta">{centralDate(h.since)}{h.note ? ` · ${h.note}` : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function AdminPlanPricing({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(api('/api/admin/plans'));
      setData(res.data);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || 'Could not load plan prices.');
    } finally {
      setLoading(false);
    }
  }, []);

  // refreshKey: AdminPanel bumps it on pull-to-refresh.
  useEffect(() => { load(); }, [load, refreshKey]);

  const drifted = data ? driftedPlans(data.plans) : [];

  return (
    <section className="pp-section" aria-labelledby="pp-heading">
      <h2 id="pp-heading">Plan pricing</h2>
      <p className="pp-intro">Shared by SnowTrack and LawnTrack. Monthly, in US dollars.</p>
      {loading && <p className="pp-meta">Loading plan prices...</p>}
      {error && <div className="alert alert-danger pp-error">{error}</div>}
      {data && data.tableMissing && (
        <div className="alert alert-warning pp-banner">
          Plan prices are not set up on this database yet (migration 092), so prices cannot be changed here.
        </div>
      )}
      {drifted.length > 0 && (
        <div className="alert alert-warning pp-banner" role="status">
          <strong>{DRIFT_TEXT}</strong>
          <div className="pp-meta">
            {drifted.map((p) => `${p.name}: ${formatCents(p.active.amountCents)} live, ${formatCents(staticCents(p.planId))} on the public pages`).join('; ')}
          </div>
        </div>
      )}
      {data && (
        <ul className="pp-list">
          {data.plans.map((plan) => <PlanRow key={plan.planId} plan={plan} onSaved={load} />)}
        </ul>
      )}
      <style>{styles}</style>
    </section>
  );
}

const styles = `
  .pp-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  .pp-section h2 { margin: 0 0 4px; font-size: 1.25rem; }
  .pp-intro { margin: 0 0 12px; font-size: 0.88rem; color: #475569; }
  .pp-banner { margin: 0 0 12px; font-size: 0.9rem; }
  .pp-error { margin: 8px 0 0; font-size: 0.85rem; }
  .pp-list { list-style: none; padding: 0; margin: 0; }
  .pp-row { padding: 12px 0; border-bottom: 1px solid #e5e7eb; }
  .pp-row:last-child { border-bottom: none; }
  .pp-main { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 16px; }
  .pp-plan { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 10px; min-width: 0; }
  .pp-name { font-weight: 700; color: #1e293b; }
  .pp-amount { font-size: 1.1rem; font-weight: 600; color: #1e293b; }
  .pp-meta { font-size: 0.8rem; color: var(--color-text-light, #6b7280); overflow-wrap: anywhere; }
  .pp-edit { display: flex; align-items: center; gap: 8px; }
  .pp-label { font-size: 0.85rem; color: #475569; margin: 0; }
  .pp-input-wrap {
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 0 8px;
    background: white;
  }
  .pp-input-wrap input {
    border: none;
    outline: none;
    width: 80px;
    padding: 6px 0;
    font-size: 16px;
    background: transparent;
  }
  .pp-history { margin-top: 6px; }
  .pp-history-toggle {
    background: none;
    border: none;
    padding: 4px 0;
    color: #2563eb;
    font-size: 0.82rem;
    cursor: pointer;
  }
  .pp-history ul { list-style: none; padding: 0; margin: 4px 0 0; }
  .pp-history li {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 2px 12px;
    padding: 4px 0;
    font-size: 0.85rem;
    border-bottom: 1px solid #f1f5f9;
  }
  @media (max-width: 640px) {
    .pp-section { padding: 16px; }
    .pp-edit { width: 100%; }
    .pp-input-wrap { flex: 1; }
    .pp-input-wrap input { width: 100%; }
  }
`;

export default AdminPlanPricing;
